import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
} from '@mui/material';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined';

import { dataTableSx, HeadLabelCell, stickyTotalCellSx } from '../../../shared/components/TableHeader';
import {
  EMPTY_VALUE, formatEf, formatLitre, formatTonne,
} from '../../../shared/utils/number';
import { SCOPE_TOKENS } from '../../../theme/muiTheme';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import useScope1Emissions from '../hooks/useScope1Emissions';
import EmissionPanel from './EmissionPanel';
import EmissionOverviewNotice from './EmissionOverviewNotice';
import ScopeHero from './ScopeHero';
import Scope1TrendChart from './Scope1TrendChart';

const ROWS_PER_PAGE = 15;
const EMPTY_COMPANIES = [];

export default function Scope1Page() {
  const { t } = useTranslation();
  const {
    year, setYear, years, data, loading, error, reload,
  } = useScope1Emissions();
  const scope = data.scope1;
  const companies = scope.companies || EMPTY_COMPANIES;
  const isHistorical = Boolean(data.meta.isHistorical);
  const [page, setPage] = useState(0);
  const visibleCompanies = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return companies.slice(start, start + ROWS_PER_PAGE);
  }, [page, companies]);
  useEffect(() => setPage(0), [year, companies]);
  const hasData = data.meta.status.scope1 !== 'EMPTY';
  const anomalies = data.meta.anomalies.filter((item) => item.startsWith('SCOPE1'));
  usePagePresentation({
    loading,
    status: error ? null : data.meta.status.scope1,
    estimation: error ? null : data.meta.estimatedFactors.scope1,
  });
  const canRenderContent = !error;
  const totalVolume = companies.reduce((sum, item) => sum + item.volumeLitres, 0);

  return (
    <Box component="section" aria-label={t('scopeEmissions.scope1.title')} className="page-stack">
      <ScopeHero
        title={t('scopeEmissions.scope1.title')}
        total={scope.total}
        year={year}
        years={years}
        onYearChange={setYear}
        tone={SCOPE_TOKENS.SCOPE_1}
        icon={LocalFireDepartmentOutlinedIcon}
        yearLabel={t('scopeEmissions.common.reportingYear')}
      />

      <EmissionOverviewNotice
        error={error}
        hasData={hasData}
        anomalies={anomalies}
        onRetry={reload}
      />

      {canRenderContent && (
        <>
      {!isHistorical && <EmissionPanel
        title={t('scopeEmissions.scope1.chartTitle')}
        icon={StackedLineChartOutlinedIcon}
        unit="L / tCO₂e"
      >
        <Scope1TrendChart
          data={scope.monthly}
          color={SCOPE_TOKENS.SCOPE_1.color}
          ariaLabel={t('scopeEmissions.scope1.chartAria', { year })}
        />
      </EmissionPanel>}

      <EmissionPanel
        title={t('scopeEmissions.scope1.tableTitle')}
        icon={LocalGasStationOutlinedIcon}
      >
        <TableContainer sx={{ maxHeight: 460, overflow: 'auto' }}>
          <Table
            stickyHeader
            size="small"
            sx={(theme) => ({ ...dataTableSx(theme), minWidth: 620 })}
          >
            <TableHead>
              <TableRow>
                <HeadLabelCell>{t('scopeEmissions.scope1.column.company')}</HeadLabelCell>
                {!isHistorical && <HeadLabelCell align="right">{t('scopeEmissions.scope1.column.volume')}</HeadLabelCell>}
                {!isHistorical && <HeadLabelCell align="right">{t('scopeEmissions.scope1.column.factor')}</HeadLabelCell>}
                <HeadLabelCell align="right">{t('scopeEmissions.scope1.column.emissions')}</HeadLabelCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={isHistorical ? 2 : 5} sx={{ color: 'text.secondary', py: 3 }}>
                    {t('emissionOverview.empty')}
                  </TableCell>
                </TableRow>
              )}
              {visibleCompanies.map((company) => (
                <TableRow key={company.id}>
                  <TableCell sx={{ fontWeight: 650 }}>{company.companyCode || company.legalEntity}</TableCell>
                  {!isHistorical && <TableCell align="right">{formatLitre(company.volumeLitres)}</TableCell>}
                  {!isHistorical && <TableCell align="right">
                    {company.emissionFactor === null ? EMPTY_VALUE : formatEf(company.emissionFactor)}
                  </TableCell>}
                  <TableCell align="right" sx={{ color: SCOPE_TOKENS.SCOPE_1.dark, fontWeight: 750 }}>
                    {formatTonne(company.emissions)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={(theme) => ({ ...stickyTotalCellSx(theme), fontWeight: 800 })}>{t('scopeEmissions.common.total')}</TableCell>
                {!isHistorical && <TableCell align="right" sx={(theme) => ({ ...stickyTotalCellSx(theme), fontWeight: 800 })}>{formatLitre(totalVolume)}</TableCell>}
                {!isHistorical && <TableCell sx={stickyTotalCellSx} />}
                <TableCell align="right" sx={(theme) => ({ ...stickyTotalCellSx(theme), fontWeight: 800 })}>{formatTonne(scope.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={companies.length}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[]}
        />
      </EmissionPanel>
        </>
      )}
    </Box>
  );
}
